import React, { forwardRef, useImperativeHandle, useMemo, useState, useRef } from 'react';
import {
	Button,
	Card,
	Col,
	Divider,
	Drawer,
	Input,
	List,
	Pagination,
	Row,
	Select,
	Space,
	Switch,
	Tag,
	Typography,
	message,
	Tooltip,
} from 'antd';
import {
	DeleteOutlined,
	DragOutlined,
	PlusOutlined,
	ReloadOutlined,
	SaveOutlined,
	EditOutlined,
} from '@ant-design/icons';

import SectionEditorDrawer from '../section_premium/SectionEditorDrawer';

import {
	DndContext,
	DragOverlay,
	PointerSensor,
	useDraggable,
	useDroppable,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import sectionApiV2 from '../../api/secitonV2';
import pageSectionLinkApi from '../../api/pageSectionLinkV2';

const { Text, Title } = Typography;



export const SECTION_TYPE_LABEL = {
	CONTENT_HTML: 'Nội dung (văn bản / bài viết)',
	DOWNLOAD_LIST: 'Danh sách tải về (file)',
	PROJECT_INTRO_2BLOCK: 'Giới thiệu (2 khối hình + nội dung)',
	PRODUCT_SWIPER: 'Sản phẩm dạng trượt (slider)',
	NEWS_SWIPER: 'Tin tức dạng trượt (slider)',
	NEWS_HERO_PLUS_3: 'Tin nổi bật + 3 tin',
	CATEGORY_GRID: 'Lưới danh mục',
	LINK_CARDS: 'Thẻ liên kết (banner/link)',
};

export const SECTION_TYPE_OPTIONS = [
	{ label: SECTION_TYPE_LABEL.CONTENT_HTML, value: 'CONTENT_HTML' },
	{ label: SECTION_TYPE_LABEL.DOWNLOAD_LIST, value: 'DOWNLOAD_LIST' },
	{ label: SECTION_TYPE_LABEL.PROJECT_INTRO_2BLOCK, value: 'PROJECT_INTRO_2BLOCK' },
	{ label: SECTION_TYPE_LABEL.PRODUCT_SWIPER, value: 'PRODUCT_SWIPER' },
	{ label: SECTION_TYPE_LABEL.NEWS_SWIPER, value: 'NEWS_SWIPER' },
	{ label: SECTION_TYPE_LABEL.NEWS_HERO_PLUS_3, value: 'NEWS_HERO_PLUS_3' },
	{ label: SECTION_TYPE_LABEL.CATEGORY_GRID, value: 'CATEGORY_GRID' },
	{ label: SECTION_TYPE_LABEL.LINK_CARDS, value: 'LINK_CARDS' },
];

const renderTypeTag = (type) => (
	<Tooltip title={type}>
		<Tag color={getTypeTagColor(type)} style={{ marginRight: 0, borderRadius: 999 }}>
			{SECTION_TYPE_LABEL[type] || type}
		</Tag>
	</Tooltip>
);

const getTypeTagColor = (type) => {
	const map = {
		CONTENT_HTML: 'magenta',
		DOWNLOAD_LIST: 'volcano',
		PROJECT_INTRO_2BLOCK: 'geekblue',
		PRODUCT_SWIPER: 'blue',
		NEWS_SWIPER: 'purple',
		NEWS_HERO_PLUS_3: 'gold',
		CATEGORY_GRID: 'gold',
		LINK_CARDS: 'green',
	};

	return map[type] || 'blue';
};

function getSectionTitle(sec) {
	const vi = sec?.props?.title?.vi;
	const en = sec?.props?.title?.en;
	return vi || en || sec?.name || '(no title)';
}

function prefixSel(id) {
	return `sel-${id}`;
}
function prefixLib(id) {
	return `lib-${id}`;
}
function unprefix(id) {
	return String(id).replace(/^sel-/, '').replace(/^lib-/, '');
}

function DroppableArea({ id, children }) {
	const { setNodeRef, isOver } = useDroppable({ id });
	return (
		<div
			ref={setNodeRef}
			style={{
				padding: 8,
				borderRadius: 12,
				border: '1px dashed #d9d9d9',
				background: isOver ? '#fafafa' : 'transparent',
				minHeight: 120,
			}}
		>
			{children}
		</div>
	);
}

function LibraryDraggable({ sec, onAdd, onEdit }) {
	const draggableId = prefixLib(sec._id);

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useDraggable({
		id: draggableId,
		data: { from: 'library', section: sec, sectionId: sec._id },
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
		cursor: 'grab',
	};

	return (
		<div ref={setNodeRef} style={style}>
			<Card size="small" style={{ marginBottom: 8 }}>
				<Row align="middle" justify="space-between" gutter={8}>
					<Col flex="auto">
						<Space wrap>
							<Text strong>{getSectionTitle(sec)}</Text>
							{sec?.type ? renderTypeTag(sec.type) : null}
						</Space>
						<div style={{ marginTop: 4 }}>
							<Text type="secondary" style={{ fontSize: 12 }}>
								{sec?.name || sec?._id}
							</Text>
						</div>
					</Col>
					<Col>
						<Space>
							<Button type="primary" icon={<PlusOutlined />} onClick={() => onAdd(sec)}>
								Thêm
							</Button>
							<Button icon={<EditOutlined />} onClick={() => onEdit?.(sec)}>
								Sửa
							</Button>
							<Button icon={<DragOutlined />} {...attributes} {...listeners} />
						</Space>
					</Col>
				</Row>
			</Card>
		</div>
	);
}

function SelectedSortableItem({ item, index, onRemove, onToggleEnabled }) {
	const id = prefixSel(item.sectionId);

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id,
		data: { from: 'selected', sectionId: item.sectionId },
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.6 : 1,
	};

	return (
		<div ref={setNodeRef} style={style}>
			<Card size="small" style={{ marginBottom: 8 }}>
				<Row align="middle" justify="space-between" gutter={8}>
					<Col flex="auto">
						<Space wrap>
							<Text strong>
								#{index + 1} — {item.title}
							</Text>
							{item.type ? renderTypeTag(item.type) : null}
							<Tag color={item.enabled ? 'green' : 'red'}>{item.enabled ? 'ON' : 'OFF'}</Tag>
						</Space>
						{item.sectionName ? (
							<div style={{ marginTop: 4 }}>
								<Text type="secondary" style={{ fontSize: 12 }}>
									{item.sectionName}
								</Text>
							</div>
						) : null}
					</Col>

					<Col>
						<Space>
							<Switch checked={!!item.enabled} onChange={(v) => onToggleEnabled(item.sectionId, v)} />
							<Button danger icon={<DeleteOutlined />} onClick={() => onRemove(item.sectionId)} />
							<Button icon={<DragOutlined />} {...attributes} {...listeners} />
						</Space>
					</Col>
				</Row>
			</Card>
		</div>
	);
}

const PageSectionLinkPremium = forwardRef(function PageSectionLinkPremium(_, ref) {
	const [open, setOpen] = useState(false);
	const [pageDoc, setPageDoc] = useState(null);

	const sectionEditorRef = useRef(null);

	// library (sections)
	const [libLoading, setLibLoading] = useState(false);
	const [libItems, setLibItems] = useState([]);
	const [libPg, setLibPg] = useState({ page: 1, limit: 10, total: 0 });
	const [libFilters, setLibFilters] = useState({ search: '', type: undefined });

	// selected
	const [selectedLoading, setSelectedLoading] = useState(false);
	const [selected, setSelected] = useState([]);

	// DnD
	const [activeDrag, setActiveDrag] = useState(null);
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

	const selectedSortableIds = useMemo(() => selected.map((x) => prefixSel(x.sectionId)), [selected]);

	const fetchLibrary = async (override = {}) => {
		const page = override.page ?? libPg.page;
		const limit = override.limit ?? libPg.limit;
		const filters = override.filters ?? libFilters;

		try {
			setLibLoading(true);
			const res = await sectionApiV2.get({
				page,
				limit,
				search: filters.search || '',
				type: filters.type,
				sort: -1,
			});

			const items = res?.data?.data?.items || [];
			const pg = res?.data?.data?.pagination || {};

			setLibItems(items);
			setLibPg({ page: pg.page || page, limit: pg.limit || limit, total: pg.total || 0 });
			setLibFilters(filters);
		} catch (e) {
			message.error(e?.message || 'Load sections failed');
		} finally {
			setLibLoading(false);
		}
	};

	const fetchSelected = async (pageId) => {
		try {
			setSelectedLoading(true);
			const res = await pageSectionLinkApi.listByAdmin({ pageId });
			const items = res?.data?.data?.items || [];

			const norm = items
				.map((l, idx) => {
					const secObj = l?.sectionId && typeof l.sectionId === 'object' ? l.sectionId : null;
					const sid = secObj?._id || l?.sectionId;

					return {
						sectionId: sid,
						enabled: l?.enabled ?? true,
						order: typeof l?.order === 'number' ? l.order : idx + 1,
						overrideProps: l?.overrideProps ?? null,
						title: secObj ? getSectionTitle(secObj) : '(unknown section)',
						type: secObj?.type,
						sectionName: secObj?.name,
					};
				})
				.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

			const map = new Map();
			for (const x of norm) map.set(x.sectionId, x);
			setSelected(Array.from(map.values()));
		} catch (e) {
			message.error(e?.message || 'Load page-section-links failed');
			setSelected([]);
		} finally {
			setSelectedLoading(false);
		}
	};

	const openForPage = async (page) => {
		const id = page?._id;
		if (!id) return;

		setPageDoc(page);
		setOpen(true);

		fetchLibrary({ page: 1, limit: 10, filters: { search: '', type: undefined } });
		fetchSelected(id);
	};

	useImperativeHandle(ref, () => ({
		open: openForPage,
		close: () => setOpen(false),
	}));

	const addSectionToSelected = (sec) => {
		if (!sec?._id) return;
		setSelected((prev) => {
			if (prev.some((x) => x.sectionId === sec._id)) return prev;
			return [
				...prev,
				{
					sectionId: sec._id,
					enabled: true,
					order: prev.length + 1,
					overrideProps: null,
					title: getSectionTitle(sec),
					type: sec.type,
					sectionName: sec.name,
				},
			];
		});
	};

	const removeSelected = (sectionId) => {
		setSelected((prev) => prev.filter((x) => x.sectionId !== sectionId));
	};

	const toggleEnabled = (sectionId, v) => {
		setSelected((prev) => prev.map((x) => (x.sectionId === sectionId ? { ...x, enabled: !!v } : x)));
	};

	const saveBatch = async () => {
		const pageId = pageDoc?._id;
		if (!pageId) return;

		const items = selected.map((x, idx) => ({
			pageId,
			sectionId: x.sectionId,
			enabled: x.enabled ?? true,
			order: idx + 1,
			overrideProps: x.overrideProps ?? null,
		}));

		try {
			setSelectedLoading(true);
			await pageSectionLinkApi.batch({ pageId, items });
			message.success('Đã lưu sections cho page');
			fetchSelected(pageId);
		} catch (e) {
			message.error(e?.response?.data?.message || e?.message || 'Save failed');
		} finally {
			setSelectedLoading(false);
		}
	};

	const onDragStart = (evt) => {
		const { active } = evt;
		const data = active?.data?.current;
		if (!data) return;

		if (data.from === 'library') {
			const sec = data.section;
			setActiveDrag({
				from: 'library',
				sectionId: data.sectionId,
				title: getSectionTitle(sec),
				type: sec?.type,
			});
		} else if (data.from === 'selected') {
			const sid = unprefix(active.id);
			const found = selected.find((x) => x.sectionId === sid);
			setActiveDrag({
				from: 'selected',
				sectionId: sid,
				title: found?.title,
				type: found?.type,
			});
		}
	};

	const onDragEnd = (evt) => {
		const { active, over } = evt;
		setActiveDrag(null);

		if (!active || !over) return;

		const overId = String(over.id);
		const isOverSelectedArea = overId === 'selected-droppable' || overId.startsWith('sel-');

		const from = active?.data?.current?.from;

		// library -> selected
		if (from === 'library' && isOverSelectedArea) {
			const sec = active?.data?.current?.section;
			addSectionToSelected(sec);
			return;
		}

		// reorder selected
		if (from === 'selected' && isOverSelectedArea) {
			const activeId = String(active.id);
			if (!activeId.startsWith('sel-')) return;
			if (!overId.startsWith('sel-')) return;

			const oldIndex = selectedSortableIds.indexOf(activeId);
			const newIndex = selectedSortableIds.indexOf(overId);
			if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;

			setSelected((prev) => arrayMove(prev, oldIndex, newIndex));
		}
	};

	const onDragCancel = () => setActiveDrag(null);

	return (
		<Drawer
			title={
				<Space direction="vertical" size={0}>
					<Title level={4} style={{ margin: 0 }}>
						Quản lý khối nội dung của trang
					</Title>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Trang: {pageDoc?.names?.vi || pageDoc?.names?.en || pageDoc?.path || pageDoc?._id}
					</Text>
				</Space>
			}
			open={open}
			width={1150}
			onClose={() => setOpen(false)}
			destroyOnHidden
			extra={
				<Space>
					<Button icon={<ReloadOutlined />} onClick={() => pageDoc?._id && fetchSelected(pageDoc._id)}>
						Tải lại links
					</Button>
					<Button danger onClick={() => setSelected([])}>
						Xoá hết
					</Button>
					<Button type="primary" icon={<SaveOutlined />} loading={selectedLoading} onClick={saveBatch}>
						Lưu
					</Button>
				</Space>
			}
		>
			<DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
				<Row gutter={16}>
					{/* LEFT: Library */}
					<Col span={12}>
						<Card
							title="Thư viện khối nội dung"
							extra={
								<Button
									icon={<ReloadOutlined />}
									loading={libLoading}
									onClick={() => fetchLibrary({ page: 1 })}
								>
									Refresh
								</Button>
							}
						>
							<Row gutter={8}>
								<Col span={14}>
									<Input
										allowClear
										placeholder="Tìm kiếm theo tên"
										value={libFilters.search}
										onChange={(e) => setLibFilters((p) => ({ ...p, search: e.target.value }))}
										onPressEnter={() => fetchLibrary({ page: 1, filters: { ...libFilters } })}
									/>
								</Col>
								<Col span={10}>
									<Select
										allowClear
										style={{ width: '100%' }}
										placeholder="Loại khối nội dung"
										value={libFilters.type}
										onChange={(v) => {
											const next = { ...libFilters, type: v };
											setLibFilters(next);
											fetchLibrary({ page: 1, filters: next });
										}}
										options={SECTION_TYPE_OPTIONS.map((t) => ({ value: t.value, label: t.label }))}
									/>
								</Col>
							</Row>

							<Divider style={{ margin: '12px 0' }} />

							<DroppableArea id="library-droppable">
								<List
									loading={libLoading}
									dataSource={libItems}
									renderItem={(sec) => (
										<List.Item style={{ padding: 0, border: 'none' }}>
											<LibraryDraggable
												sec={sec}
												onAdd={addSectionToSelected}
												onEdit={(s) => sectionEditorRef.current?.openEdit?.(s?._id)}
											/>
										</List.Item>
									)}
								/>
							</DroppableArea>

							<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
								<Pagination
									current={libPg.page}
									pageSize={libPg.limit}
									total={libPg.total}
									showSizeChanger
									pageSizeOptions={[10, 20, 50, 100]}
									onChange={(page, pageSize) => fetchLibrary({ page, limit: pageSize })}
								/>
							</div>

							<Text type="secondary" style={{ fontSize: 12 }}>
								Tip: Bấm “Thêm” hoặc kéo thả sang cột bên phải.
							</Text>
						</Card>
					</Col>

					{/* RIGHT: Selected */}
					<Col span={12}>
						<Card title="Khối nội dung trong trang (kéo để sắp xếp)">
							<DroppableArea id="selected-droppable">
								{selected.length === 0 ? (
									<Text type="secondary">
										Chưa có khối nội dung nào. Kéo từ Library qua hoặc bấm “Thêm”.
									</Text>
								) : (
									<SortableContext items={selectedSortableIds} strategy={verticalListSortingStrategy}>
										{selected.map((it, idx) => (
											<SelectedSortableItem
												key={it.sectionId}
												item={it}
												index={idx}
												onRemove={removeSelected}
												onToggleEnabled={toggleEnabled}
											/>
										))}
									</SortableContext>
								)}
							</DroppableArea>

							<Divider style={{ margin: '12px 0' }} />
							<Text type="secondary" style={{ fontSize: 12 }}>
								Thứ tự hiển thị sẽ được lưu theo vị trí (order = index + 1).
							</Text>
						</Card>
					</Col>
				</Row>

				<DragOverlay>
					{activeDrag ? (
						<Card size="small" style={{ width: 320 }}>
							<Space>
								<DragOutlined />
								<div>
									<Text strong>{activeDrag.title}</Text>
									<div>{activeDrag.type ? <Tag color="blue">{activeDrag.type}</Tag> : null}</div>
								</div>
							</Space>
						</Card>
					) : null}
				</DragOverlay>
			</DndContext>

			{/* section */}
			<SectionEditorDrawer
				ref={sectionEditorRef}
				onSaved={() => {
					fetchLibrary({ page: libPg.page, limit: libPg.limit, filters: libFilters });
					if (pageDoc?._id) fetchSelected(pageDoc._id);
				}}
			/>
		</Drawer>
	);
});

export default PageSectionLinkPremium;
